import React, { useMemo } from "react";
import styled from "styled-components";
import { get, isEqual, omit } from "lodash";
import { useHovered } from "@sonder/ui/source/hooks/useHovered";
import { Checkmark, CircleX } from "@sonder/ui/source/icons";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { DropdownContent } from "@sonder/ui/source/styled/components/DropdownContent";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import { ButtonList } from "@sonder/ui/source/styled/fragments/ButtonList";
import { CancelButton } from "@sonder/ui/source/styled/fragments/CancelButton";
import { DropdownContentAlignment } from "@sonder/ui/source/styled/surfaces/DropdownContent";
import { colors } from "@sonder/ui/source/styled/theme";
import { useSegment } from "analytics/segment";
import { useStore as useAuthStore } from "auth/store";
import { useDeleteSavedFilter } from "core/queries/DeleteSavedFilter";
import { SavedFilters, useGetSavedFilters } from "core/queries/GetSavedFilters";
import { bulkSetFilters } from "tasks/actions";
import { useStore } from "tasks/store";
import { FilterOwner } from "typings/globalGQLTypes";

const StyledFilterButton = styled(Button)`
  height: auto;
  justify-content: flex-start;
  min-height: 2.3rem;
  padding-right: 1.7rem;
  position: relative;
  text-align: left;

  svg {
    height: 1rem;
    width: 1rem;
  }
`;

const StyledDropdownContent = styled(DropdownContent)`
  flex-direction: column;
  min-width: 20rem;

  ${StyledFilterButton} + ${StyledFilterButton} {
    margin-top: 1rem;
  }
`;

const StyledTitle = styled.h5`
  margin: 0;
  padding: 1rem 1rem;
`;

const StyledButtonList = styled(ButtonList)`
  max-height: 16.5rem;
  padding: 0 1rem 1rem;
`;

const StyledButton = styled(Button)`
  color: ${colors.blue7};
`;

const StyledCheckmark = styled.span`
  color: ${colors.blue7};
  margin-right: 0.5rem;
`;

const StyledCancelButton = styled(CancelButton)`
  position: absolute;
  right: 0.7rem;
  top: 50%;
  transform: translateY(-50%);
`;

const StyledEmptyState = styled.div`
  color: ${colors.gray6};
  padding: 0 1rem 1rem;
  text-align: center;
`;

interface FilterButtonProps {
  filter: SavedFilters;
  onChange?(): void;
  onDelete?(): void;
  isSelected?: boolean;
}

interface SavedFilterPickerProps {
  alignContent?: DropdownContentAlignment;
  eventName?: string;
}

const FilterButton = ({
  filter,
  onChange,
  onDelete,
  isSelected,
}: FilterButtonProps) => {
  const { hovered, onMouseEnter, onMouseLeave } = useHovered();
  const { state: authState } = useAuthStore();
  const isTOMMember = get(authState, "user.isTOMMember", false);
  return (
    <StyledFilterButton
      fullWidth
      noBorder
      title={filter.description || undefined}
      onClick={onChange}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {isSelected && (
        <StyledCheckmark>
          <Checkmark />
        </StyledCheckmark>
      )}
      <span>{filter.name}</span>
      {hovered && isTOMMember && (
        <StyledCancelButton
          onClick={e => {
            e.stopPropagation();
            if (onDelete) {
              onDelete();
            }
          }}
        >
          <CircleX />
        </StyledCancelButton>
      )}
    </StyledFilterButton>
  );
};

export const SavedFilterPicker = ({
  alignContent = "right",
  eventName = "saved_filter_picker",
}: SavedFilterPickerProps) => {
  const { track } = useSegment();
  const {
    dispatch,
    state: { filters: globalFilters },
  } = useStore();
  const { state: authState } = useAuthStore();
  const isTOMMember = get(authState, "user.isTOMMember", false);
  const cityId = get(authState, "user.cityId", null);
  const { data, loading } = useGetSavedFilters({
    skip: !cityId,
    variables: {
      ownerId: cityId,
      ownerType: FilterOwner.city,
    },
  });
  const [onDeleteSavedFilter] = useDeleteSavedFilter({
    refetchQueries: ["GetSavedFilters"],
    awaitRefetchQueries: true,
  });
  const filters = get(data, "savedTomFilters") || [];
  const hasNoFilters = filters.length === 0;
  const selectedFilter = useMemo(
    () =>
      filters.find(filter =>
        isEqual(omit(filter.filter, "__typename"), globalFilters),
      ),
    [filters, globalFilters],
  );

  return (
    <Dropdown
      trigger={<StyledButton noBorder>Saved filters</StyledButton>}
      onClose={() => {
        track({
          eventName,
          action: "click",
          element: "saved_filter_picker.dropdown_trigger",
          props: { nextValue: "closed" },
        });
      }}
      onOpen={() => {
        track({
          eventName,
          action: "click",
          element: "saved_filter_picker.dropdown_trigger",
          props: { nextValue: "opened" },
        });
      }}
    >
      {({ isOpen: isPortalOpen }) => {
        return (
          isPortalOpen && (
            <StyledDropdownContent align={alignContent}>
              <StyledTitle>
                {isTOMMember ? "Saved filters" : "Presets"}
              </StyledTitle>
              {hasNoFilters && (
                <StyledEmptyState>
                  {loading
                    ? "Loading..."
                    : "There are currently no saved filters."}
                </StyledEmptyState>
              )}
              {!hasNoFilters && (
                <StyledButtonList>
                  {filters.map(filter => (
                    <FilterButton
                      filter={filter}
                      isSelected={
                        selectedFilter && selectedFilter.id === filter.id
                      }
                      key={filter.id}
                      onChange={() => {
                        dispatch(
                          bulkSetFilters(omit(filter.filter, "__typename")),
                        );
                        track({
                          eventName,
                          action: "click",
                          element: "saved_filter_picker.select_filter",
                          props: { nextValue: filter.id },
                        });
                      }}
                      onDelete={() => {
                        onDeleteSavedFilter({
                          variables: {
                            id: filter.id,
                          },
                        });
                        track({
                          eventName,
                          action: "click",
                          element: "saved_filter_picker.delete_filter",
                          props: { nextValue: filter.id },
                        });
                      }}
                    />
                  ))}
                </StyledButtonList>
              )}
            </StyledDropdownContent>
          )
        );
      }}
    </Dropdown>
  );
};
